import React, { useEffect, useMemo, useRef, useState } from 'react';
import API_BASE_URL from '../../config/api';
const NAVER_MAP_CLIENT_ID = 'hs17twm61o';
const CAPACITY_BAND_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'ge_100', label: '100kW 이상' },
  { value: 'ge_500', label: '500kW 이상' },
  { value: 'ge_1000', label: '1MW 이상' },
  { value: 'ge_3000', label: '3MW 이상' },
];

function safeText(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function loadScript(src, attrName) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[${attrName}="true"]`);

    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }

      const onLoad = () => resolve();
      const onError = () => reject(new Error(`스크립트를 불러오지 못했습니다: ${src}`));

      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', onError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute(attrName, 'true');

    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };

    script.onerror = () => {
      reject(new Error(`스크립트를 불러오지 못했습니다: ${src}`));
    };

    document.head.appendChild(script);
  });
}

async function loadNaverMapSdk() {
  if (window.naver?.maps?.Map) return;

  await loadScript(
    `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${NAVER_MAP_CLIENT_ID}`,
    'data-naver-map-sdk'
  );
}

async function loadMarkerClustering() {
  if (window.MarkerClustering) return;

  await loadScript('/MarkerClustering.js', 'data-marker-clustering-sdk');

  if (typeof window.MarkerClustering !== 'function') {
    throw new Error('MarkerClustering.js 로드 후에도 window.MarkerClustering이 없습니다.');
  }
}

function cross(o, a, b) {
  return (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);
}

function buildConvexHull(points) {
  if (!points || points.length < 3) return points || [];

  const sorted = [...points].sort((p1, p2) => {
    if (p1.lng !== p2.lng) return p1.lng - p2.lng;
    return p1.lat - p2.lat;
  });

  const lower = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();

  return [...lower, ...upper];
}

function distanceSq(a, b) {
  const dx = a.lng - b.lng;
  const dy = a.lat - b.lat;
  return dx * dx + dy * dy;
}

function buildNearestNeighborPath(points) {
  if (!points || points.length <= 1) return points || [];

  const remaining = [...points];
  remaining.sort((a, b) => (a.lng === b.lng ? a.lat - b.lat : a.lng - b.lng));

  const path = [remaining.shift()];

  while (remaining.length > 0) {
    const last = path[path.length - 1];
    let bestIndex = 0;
    let bestDist = distanceSq(last, remaining[0]);

    for (let i = 1; i < remaining.length; i += 1) {
      const d = distanceSq(last, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIndex = i;
      }
    }

    path.push(remaining.splice(bestIndex, 1)[0]);
  }

  return path;
}

function samplePoints(points, maxCount) {
  if (!points || points.length <= maxCount) return points || [];
  const step = points.length / maxCount;
  const sampled = [];

  for (let i = 0; i < maxCount; i += 1) {
    sampled.push(points[Math.floor(i * step)]);
  }

  return sampled;
}

export default function NaverGridMap({ source = 'ess' }) {
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const markersRef = useRef([]);
  const clusterRef = useRef(null);

  const polygonOverlaysRef = useRef([]);
  const polylineOverlaysRef = useRef([]);

  const pointsRef = useRef([]);
  const renderTimerRef = useRef(null);
  const idleListenerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pointsCacheRef = useRef(new Map());
  const capacityViewModeRef = useRef('map');
  const capacityBandRef = useRef('');

  const [pairOptions, setPairOptions] = useState([]);
  const [selectedPair, setSelectedPair] = useState('');
  const [pointCount, setPointCount] = useState(0);
  const [renderedCount, setRenderedCount] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [loadingMap, setLoadingMap] = useState(true);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showClusters, setShowClusters] = useState(true);
  const [selectedCapacityBand, setSelectedCapacityBand] = useState('');
  const [capacityViewMode, setCapacityViewMode] = useState('map');
  const [addressQuery, setAddressQuery] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [loadingAddressSearch, setLoadingAddressSearch] = useState(false);
  const [selectedAddressPoint, setSelectedAddressPoint] = useState(null);

  const [showArea, setShowArea] = useState(false);
  const [showLine, setShowLine] = useState(false);

  const DETAIL_MIN_ZOOM = 8;
  const CLUSTER_MAX_ZOOM = 17;
  const MAX_VISIBLE_POINTS = 1800;
  const FETCH_LIMIT = 15000;
  const MAX_SHAPE_POINTS = 300;
  const isCapacityMode = source === 'capacity';

  capacityViewModeRef.current = capacityViewMode;
  capacityBandRef.current = selectedCapacityBand;

  const getToken = () => localStorage.getItem('authToken');

  const clearPolygonOverlays = () => {
    polygonOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
    polygonOverlaysRef.current = [];
  };

  const clearPolylineOverlays = () => {
    polylineOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
    polylineOverlaysRef.current = [];
  };

  const clearAllOverlays = () => {
    clearPolygonOverlays();
    clearPolylineOverlays();
  };

  const clearMarkers = () => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (clusterRef.current) {
      clusterRef.current.setMap(null);
      clusterRef.current = null;
    }

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  };

  const clearAllMapObjects = () => {
    clearMarkers();
    clearAllOverlays();
  };

  const getBufferedBounds = () => {
    const map = mapRef.current;
    if (!map) return null;

    const bounds = map.getBounds();
    if (!bounds) return null;

    const sw = bounds.getSW();
    const ne = bounds.getNE();

    const latPad = (ne.lat() - sw.lat()) * 0.15;
    const lngPad = (ne.lng() - sw.lng()) * 0.15;

    return {
      minLat: sw.lat() - latPad,
      maxLat: ne.lat() + latPad,
      minLng: sw.lng() - lngPad,
      maxLng: ne.lng() + lngPad,
    };
  };

  const isInBounds = (p, b) => {
    return (
      p.lat >= b.minLat &&
      p.lat <= b.maxLat &&
      p.lng >= b.minLng &&
      p.lng <= b.maxLng
    );
  };

  const createMarker = (point) => {
    const marker = new window.naver.maps.Marker({
      position: new window.naver.maps.LatLng(point.lat, point.lng),
      map: showClusters ? mapRef.current : null,
    });

    const content = `
      <div style="padding:10px;min-width:240px;font-size:13px;line-height:1.5;color:#0f172a;">
        <div><b>변전소:</b> ${safeText(point.subst) || '-'}</div>
        <div><b>배전선로:</b> ${safeText(point.line) || '-'}</div>
        <div><b>주소:</b> ${safeText(point.address) || '-'}</div>
        <div><b>주변압기:</b> ${safeText(point.mtr) || '-'}</div>
        ${source === 'capacity' ? `<div><b>선로여유용량:</b> ${Number(point.line_available_kw || 0).toLocaleString()} kW</div>` : ''}
      </div>
    `;

    window.naver.maps.Event.addListener(marker, 'click', () => {
      if (!infoWindowRef.current || !mapRef.current) return;
      infoWindowRef.current.setContent(content);
      infoWindowRef.current.open(mapRef.current, marker);
    });

    return marker;
  };

  const rebuildCluster = (markers) => {
    if (clusterRef.current) {
      clusterRef.current.setMap(null);
      clusterRef.current = null;
    }

    const icons = [
      '<div class="cluster-marker cluster-1"></div>',
      '<div class="cluster-marker cluster-2"></div>',
      '<div class="cluster-marker cluster-3"></div>',
      '<div class="cluster-marker cluster-4"></div>',
      '<div class="cluster-marker cluster-5"></div>',
    ].map((html) => ({
      content: html,
      size: new window.naver.maps.Size(44, 44),
      anchor: new window.naver.maps.Point(22, 22),
    }));

    clusterRef.current = new window.MarkerClustering({
      minClusterSize: 2,
      maxZoom: CLUSTER_MAX_ZOOM,
      map: showClusters ? mapRef.current : null,
      markers,
      gridSize: 100,
      icons,
      indexGenerator: [10, 50, 100, 300, 1000],
      stylingFunction(clusterMarker, count) {
        const el = clusterMarker.getElement?.();
        if (!el) return;
        const div = el.querySelector('div');
        if (div) div.textContent = String(count);
      },
    });
  };

  const shapeSourcePoints = useMemo(() => {
    return samplePoints(pointsRef.current, MAX_SHAPE_POINTS);
  }, [pointCount]);

  const drawPolygonOverlay = () => {
    clearPolygonOverlays();

    if (!showArea || !mapRef.current) return;
    if (!shapeSourcePoints || shapeSourcePoints.length < 3) return;

    const hull = buildConvexHull(shapeSourcePoints);
    if (!hull || hull.length < 3) return;

    const polygon = new window.naver.maps.Polygon({
      map: mapRef.current,
      paths: hull.map((p) => new window.naver.maps.LatLng(p.lat, p.lng)),
      fillColor: '#16a34a',
      fillOpacity: 0.18,
      strokeColor: '#15803d',
      strokeOpacity: 0.95,
      strokeWeight: 2,
      clickable: true,
    });

    polygonOverlaysRef.current.push(polygon);
  };

  const drawPolylineOverlay = () => {
    clearPolylineOverlays();

    if (!showLine || !mapRef.current) return;
    if (!shapeSourcePoints || shapeSourcePoints.length < 2) return;

    const ordered = buildNearestNeighborPath(shapeSourcePoints);
    if (!ordered || ordered.length < 2) return;

    const polyline = new window.naver.maps.Polyline({
      map: mapRef.current,
      path: ordered.map((p) => new window.naver.maps.LatLng(p.lat, p.lng)),
      strokeColor: '#ef4444',
      strokeOpacity: 0.95,
      strokeWeight: 3,
      clickable: false,
    });

    polylineOverlaysRef.current.push(polyline);
  };

  const drawOverlays = () => {
    drawPolygonOverlay();
    drawPolylineOverlay();
  };

  const renderVisibleMarkers = () => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (!showClusters) {
      clearMarkers();
      drawOverlays();
      setRenderedCount(0);
      return;
    }

    const zoom = map.getZoom();

    if (zoom < DETAIL_MIN_ZOOM) {
      clearMarkers();
      drawOverlays();
      setRenderedCount(0);
      return;
    }

    const buffered = getBufferedBounds();
    if (!buffered) return;

    let visiblePoints = pointsRef.current.filter((p) => isInBounds(p, buffered));

    if (visiblePoints.length > MAX_VISIBLE_POINTS) {
      visiblePoints = visiblePoints.slice(0, MAX_VISIBLE_POINTS);
    }

    clearMarkers();

    const markers = visiblePoints.map((point) => createMarker(point));
    markersRef.current = markers;
    rebuildCluster(markers);
    setRenderedCount(markers.length);

    drawOverlays();
  };

  const scheduleRender = (delay = 70) => {
    if (renderTimerRef.current) {
      clearTimeout(renderTimerRef.current);
    }

    renderTimerRef.current = setTimeout(() => {
      renderVisibleMarkers();
    }, delay);
  };

  const fetchCapacityPoints = async (options = {}) => {
    if (!mapRef.current) return;

    const token = getToken();
    if (!token) {
      setErrorMessage('로그인이 필요합니다.');
      return;
    }

    const bounds = options.bounds || getBufferedBounds();
    if (!bounds) return;

    const params = new URLSearchParams();
    params.append('source', 'capacity');
    params.append('limit', String(FETCH_LIMIT));
    params.append('includeTotal', 'true');
    params.append('minLat', String(bounds.minLat));
    params.append('maxLat', String(bounds.maxLat));
    params.append('minLng', String(bounds.minLng));
    params.append('maxLng', String(bounds.maxLng));
    if (capacityBandRef.current) {
      params.append('capacityBand', capacityBandRef.current);
    }

    try {
      setLoadingPoints(true);
      setErrorMessage('');

      const response = await fetch(`${API_BASE_URL}/api/grid/points?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!(response.ok && data.status === 'success')) {
        throw new Error(data.message || '지도 데이터 조회 실패');
      }

      const points = (data.points || [])
        .map((point) => ({
          ...point,
          lat: Number(point?.lat),
          lng: Number(point?.lng),
          line_available_kw: Number(point?.line_available_kw),
        }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

      pointsRef.current = points;
      setPointCount(Number(data.total_count || data.count || points.length));
      setSelectedAddressPoint(null);
      scheduleRender(20);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '지도 데이터를 불러오지 못했습니다.');
    } finally {
      setLoadingPoints(false);
    }
  };

  const handleAddressSearch = async () => {
    const query = addressQuery.trim();
    const token = getToken();

    if (!token) {
      setErrorMessage('로그인이 필요합니다.');
      return;
    }

    if (!query) {
      setAddressResults([]);
      setSelectedAddressPoint(null);
      setErrorMessage('주소를 입력해 주세요.');
      return;
    }

    const params = new URLSearchParams();
    params.append('query', query);
    params.append('limit', '30');
    if (selectedCapacityBand) {
      params.append('capacityBand', selectedCapacityBand);
    }

    try {
      setLoadingAddressSearch(true);
      setLoadingPoints(true);
      setErrorMessage('');

      const response = await fetch(`${API_BASE_URL}/api/grid/address-search?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!(response.ok && data.status === 'success')) {
        throw new Error(data.message || '주소 검색에 실패했습니다.');
      }

      const results = (data.results || [])
        .map((point) => ({
          ...point,
          lat: Number(point?.lat),
          lng: Number(point?.lng),
          line_available_kw: Number(point?.line_available_kw),
        }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

      setAddressResults(results);
      pointsRef.current = results;
      setPointCount(results.length);

      if (results.length > 0 && mapRef.current) {
        const bounds = new window.naver.maps.LatLngBounds();
        results.forEach((p) => {
          bounds.extend(new window.naver.maps.LatLng(p.lat, p.lng));
        });
        mapRef.current.fitBounds(bounds);
        setSelectedAddressPoint(results[0]);
      } else {
        setSelectedAddressPoint(null);
      }

      scheduleRender(20);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '주소 검색 결과를 불러오지 못했습니다.');
    } finally {
      setLoadingAddressSearch(false);
      setLoadingPoints(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    async function initMap() {
      try {
        setLoadingMap(true);
        setErrorMessage('');

        await loadNaverMapSdk();
        await loadMarkerClustering();

        if (!mounted || !mapElementRef.current) return;

        const map = new window.naver.maps.Map(mapElementRef.current, {
          center: new window.naver.maps.LatLng(36.0, 127.5),
          zoom: 8,
          zoomControl: true,
          zoomControlOptions: {
            position: window.naver.maps.Position.TOP_RIGHT,
          },
          mapTypeControl: true,
          mapTypeControlOptions: {
            position: window.naver.maps.Position.TOP_LEFT,
          },
        });

        mapRef.current = map;
        infoWindowRef.current = new window.naver.maps.InfoWindow();

        idleListenerRef.current = window.naver.maps.Event.addListener(map, 'idle', () => {
          if (source === 'capacity' && capacityViewModeRef.current === 'map') {
            fetchCapacityPoints();
            return;
          }
          scheduleRender(50);
        });

        setMapReady(true);

        setTimeout(() => {
          if (!mapRef.current) return;
          window.naver.maps.Event.trigger(mapRef.current, 'resize');
          scheduleRender(50);
        }, 200);
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message || '네이버 지도 로딩에 실패했습니다.');
      } finally {
        setLoadingMap(false);
      }
    }

    initMap();

    const handleResize = () => {
      if (!mapRef.current || !window.naver?.maps?.Event) return;
      window.naver.maps.Event.trigger(mapRef.current, 'resize');
      scheduleRender(80);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      mounted = false;
      window.removeEventListener('resize', handleResize);

      if (renderTimerRef.current) {
        clearTimeout(renderTimerRef.current);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (idleListenerRef.current && window.naver?.maps?.Event) {
        window.naver.maps.Event.removeListener(idleListenerRef.current);
      }

      clearAllMapObjects();
    };
  }, []);

  useEffect(() => {
    if (isCapacityMode) return undefined;

    async function fetchPairs() {
      const token = getToken();

      if (!token) {
        setErrorMessage('로그인이 필요합니다.');
        return;
      }

      try {
        const params = new URLSearchParams();
        params.append('source', source);
        if (source === 'capacity' && selectedCapacityBand) {
          params.append('capacityBand', selectedCapacityBand);
        }

        const finalUrl = `${API_BASE_URL}/api/grid/pairs?${params.toString()}`;
        const finalResponse = await fetch(finalUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        });

        const data = await finalResponse.json();

        if (!(finalResponse.ok && data.status === 'success')) {
          throw new Error(data.message || '변전소-배전선로 목록 조회 실패');
        }

        const cleanedPairs = (data.pairs || [])
          .map((item) => {
            const subst = safeText(item?.subst);
            const line = safeText(item?.line);

            if (!subst || !line) return null;

            return {
              subst,
              line,
              label: `${subst} - ${line}`,
              value: `${subst}|||${line}`,
            };
          })
          .filter(Boolean);

        setPairOptions(cleanedPairs);

        setSelectedPair((prev) => {
          if (cleanedPairs.some((item) => item.value === prev)) {
            return prev;
          }
          return cleanedPairs[0]?.value || '';
        });
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message || '선택 목록을 불러오지 못했습니다.');
      }
    }

    fetchPairs();
  }, [isCapacityMode, source, selectedCapacityBand]);

  useEffect(() => {
    if (isCapacityMode) return undefined;

    async function fetchPoints() {
      if (!mapReady || !selectedPair) return;

      const token = getToken();
      if (!token) return;

      const [substRaw, lineRaw] = selectedPair.split('|||');
      const subst = safeText(substRaw);
      const line = safeText(lineRaw);

      if (!subst || !line) return;

      const pairKey = `${source}|||${selectedCapacityBand}|||${subst}|||${line}`;

      try {
        setLoadingPoints(true);
        setErrorMessage('');

        if (pointsCacheRef.current.has(pairKey)) {
          const cached = pointsCacheRef.current.get(pairKey);
          pointsRef.current = cached.points;
          setPointCount(cached.count);

          clearAllMapObjects();

          if (cached.points.length > 0 && mapRef.current) {
            const bounds = new window.naver.maps.LatLngBounds();
            cached.points.forEach((p) => {
              bounds.extend(new window.naver.maps.LatLng(p.lat, p.lng));
            });
            mapRef.current.fitBounds(bounds);
          } else {
            setRenderedCount(0);
          }

          setTimeout(() => {
            if (!mapRef.current) return;
            window.naver.maps.Event.trigger(mapRef.current, 'resize');
            scheduleRender(40);
          }, 80);

          return;
        }

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const params = new URLSearchParams();
        params.append('subst', subst);
        params.append('line', line);
        params.append('limit', String(FETCH_LIMIT));
        params.append('source', source);
        if (source === 'capacity' && selectedCapacityBand) {
          params.append('capacityBand', selectedCapacityBand);
        }

        const response = await fetch(`${API_BASE_URL}/api/grid/points?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          signal: controller.signal,
        });

        const data = await response.json();

        if (!(response.ok && data.status === 'success')) {
          throw new Error(data.message || '지도 데이터 조회 실패');
        }

        const points = (data.points || [])
          .map((point) => ({
            ...point,
            lat: Number(point?.lat),
            lng: Number(point?.lng),
            line_available_kw: Number(point?.line_available_kw),
          }))
          .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

        pointsRef.current = points;
        setPointCount(Number(data.count || points.length));

        pointsCacheRef.current.set(pairKey, {
          points,
          count: Number(data.count || points.length),
        });

        clearAllMapObjects();

        if (points.length > 0 && mapRef.current) {
          const bounds = new window.naver.maps.LatLngBounds();
          points.forEach((p) => {
            bounds.extend(new window.naver.maps.LatLng(p.lat, p.lng));
          });
          mapRef.current.fitBounds(bounds);
        } else {
          setRenderedCount(0);
        }

        setTimeout(() => {
          if (!mapRef.current) return;
          window.naver.maps.Event.trigger(mapRef.current, 'resize');
          scheduleRender(40);
        }, 80);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        setErrorMessage(error.message || '지도 데이터를 불러오지 못했습니다.');
      } finally {
        setLoadingPoints(false);
      }
    }

    fetchPoints();
  }, [isCapacityMode, selectedPair, mapReady, source, selectedCapacityBand]);

  useEffect(() => {
    if (!isCapacityMode || !mapReady || capacityViewMode !== 'map') return undefined;

    pointsRef.current = [];
    setAddressResults([]);
    setSelectedAddressPoint(null);
    setPointCount(0);
    setRenderedCount(0);
    fetchCapacityPoints();
    return undefined;
  }, [isCapacityMode, mapReady, capacityViewMode, selectedCapacityBand]);

  useEffect(() => {
    if (!isCapacityMode || capacityViewMode !== 'address') return undefined;

    pointsRef.current = [];
    setPointCount(addressResults.length);
    setRenderedCount(0);
    clearAllMapObjects();
    return undefined;
  }, [isCapacityMode, capacityViewMode]);

  useEffect(() => {
    if (!mapReady) return;

    if (clusterRef.current) {
      clusterRef.current.setMap(showClusters ? mapRef.current : null);
    }

    if (!showClusters) {
      clearMarkers();
      drawOverlays();
      setRenderedCount(0);
      return;
    }

    scheduleRender(30);
  }, [showClusters, mapReady]);

  useEffect(() => {
    if (!mapReady) return;
    drawOverlays();
  }, [showArea, showLine, pointCount, mapReady]);

  const selectedLabel =
    pairOptions.find((item) => item.value === selectedPair)?.label || '-';
  const selectedCapacityLabel =
    CAPACITY_BAND_OPTIONS.find((item) => item.value === selectedCapacityBand)?.label || '전체';

  return (
    <div
      className="grid-map-shell"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <div
        className="grid-map-top-layout"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '16px',
          alignItems: 'stretch',
        }}
      >
        <div
          className="grid-map-selection-card"
          style={{
            background: '#ffffff',
            color: '#0f172a',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 18px rgba(15, 23, 42, 0.08)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              className="grid-map-selection-title"
              style={{
                margin: '0 0 16px 0',
                fontSize: '20px',
                color: '#0f172a',
                fontWeight: 700,
              }}
            >
              {isCapacityMode ? '계통 여유용량 조회' : '배전선로 지도 선택'}
            </h3>

            {isCapacityMode ? (
              <>
                <div
                  className="grid-filter-group grid-filter-group-full"
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  <label style={{ fontWeight: 600, color: '#334155' }}>
                    조회 기준
                  </label>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="dashboard-secondary-btn"
                      onClick={() => setCapacityViewMode('map')}
                      style={{
                        background: capacityViewMode === 'map' ? '#dbeafe' : undefined,
                        color: capacityViewMode === 'map' ? '#1d4ed8' : undefined,
                        borderColor: capacityViewMode === 'map' ? '#93c5fd' : undefined,
                      }}
                    >
                      지도 기준
                    </button>
                    <button
                      type="button"
                      className="dashboard-secondary-btn"
                      onClick={() => setCapacityViewMode('address')}
                      style={{
                        background: capacityViewMode === 'address' ? '#dbeafe' : undefined,
                        color: capacityViewMode === 'address' ? '#1d4ed8' : undefined,
                        borderColor: capacityViewMode === 'address' ? '#93c5fd' : undefined,
                      }}
                    >
                      주소 기준
                    </button>
                  </div>
                </div>

                <div
                  className="grid-filter-group grid-filter-group-full"
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}
                >
                  <label
                    htmlFor="grid-capacity-band-select"
                    style={{ fontWeight: 600, color: '#334155' }}
                  >
                    용량기준
                  </label>

                  <select
                    id="grid-capacity-band-select"
                    value={selectedCapacityBand}
                    onChange={(e) => setSelectedCapacityBand(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      background: '#fff',
                      color: '#0f172a',
                    }}
                  >
                    {CAPACITY_BAND_OPTIONS.map((item) => (
                      <option key={item.value || 'all'} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                {capacityViewMode === 'address' && (
                  <div
                    className="grid-filter-group grid-filter-group-full"
                    style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}
                  >
                    <label
                      htmlFor="grid-address-search"
                      style={{ fontWeight: 600, color: '#334155' }}
                    >
                      주소 검색
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        id="grid-address-search"
                        value={addressQuery}
                        onChange={(e) => setAddressQuery(e.target.value)}
                        placeholder="예: 강원특별자치도 강릉시"
                        style={{
                          flex: 1,
                          padding: '12px 14px',
                          borderRadius: '10px',
                          border: '1px solid #cbd5e1',
                          fontSize: '14px',
                          background: '#fff',
                          color: '#0f172a',
                        }}
                      />
                      <button
                        type="button"
                        className="dashboard-primary-btn"
                        onClick={handleAddressSearch}
                        disabled={loadingAddressSearch}
                      >
                        {loadingAddressSearch ? '검색 중...' : '주소 검색'}
                      </button>
                    </div>
                    {addressResults.length > 0 && (
                      <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'grid', gap: '6px' }}>
                        {addressResults.slice(0, 10).map((item) => (
                          <button
                            key={`${item.id}`}
                            type="button"
                            className="dashboard-secondary-btn"
                            onClick={() => {
                              setSelectedAddressPoint(item);
                              if (mapRef.current) {
                                mapRef.current.setCenter(new window.naver.maps.LatLng(item.lat, item.lng));
                                mapRef.current.setZoom(14);
                              }
                            }}
                            style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                          >
                            {item.address}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div
                className="grid-filter-group grid-filter-group-full"
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <label
                  htmlFor="grid-pair-select"
                  style={{ fontWeight: 600, color: '#334155' }}
                >
                  변전소 - 배전선로
                </label>

                <select
                  id="grid-pair-select"
                  value={selectedPair}
                  onChange={(e) => setSelectedPair(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    background: '#fff',
                    color: '#0f172a',
                  }}
                >
                  {pairOptions.length === 0 ? (
                    <option value="">선택 가능한 데이터가 없습니다</option>
                  ) : (
                    pairOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}

            {!isCapacityMode && (
              <div
                style={{
                  marginTop: '12px',
                  fontSize: '13px',
                  color: '#64748b',
                  lineHeight: 1.6,
                }}
              >
                지도를 보기 전에 변전소와 배전선로를 선택합니다.
              </div>
            )}

            {isCapacityMode && capacityViewMode === 'map' && (
              <div
                style={{
                  marginTop: '12px',
                  fontSize: '13px',
                  color: '#64748b',
                  lineHeight: 1.6,
                }}
              >
                지도를 이동하거나 확대/축소하면 현재 화면 범위 안의 여유용량 데이터를 다시 불러옵니다.
              </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '10px',
                marginTop: '14px',
              }}
            >
              <button
                type="button"
                onClick={() => setShowClusters((prev) => !prev)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {showClusters ? '클러스터 숨기기' : '클러스터 보이기'}
              </button>

              <button
                type="button"
                onClick={() => setShowLine((prev) => !prev)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {showLine ? '배전선로 숨기기' : '배전선로 보기'}
              </button>

              <button
                type="button"
                onClick={() => setShowArea((prev) => !prev)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {showArea ? 'Area 숨기기' : 'Area 보기'}
              </button>
            </div>
          </div>
        </div>

        <div
          className="grid-map-status-card"
          style={{
            background: '#ffffff',
            color: '#0f172a',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 18px rgba(15, 23, 42, 0.08)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div
              style={{
                fontWeight: 700,
                color: '#334155',
                fontSize: '20px',
                marginBottom: '16px',
              }}
            >
              요약 정보
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                fontSize: '14px',
                lineHeight: 1.6,
              }}
            >
              <div>
                <span style={{ color: '#475569', fontWeight: 700 }}>
                  {isCapacityMode ? '조회 기준:' : '선택 대상:'}
                </span>{' '}
                {isCapacityMode ? (capacityViewMode === 'map' ? '지도 기준' : '주소 기준') : selectedLabel}
              </div>
              {source === 'capacity' && (
                <div>
                  <span style={{ color: '#475569', fontWeight: 700 }}>용량기준:</span>{' '}
                  {selectedCapacityLabel}
                </div>
              )}
              {isCapacityMode && capacityViewMode === 'address' && (
                <div>
                  <span style={{ color: '#475569', fontWeight: 700 }}>검색 주소:</span>{' '}
                  {selectedAddressPoint?.address || addressQuery.trim() || '-'}
                </div>
              )}
              <div>
                <span style={{ color: '#475569', fontWeight: 700 }}>전체 조회 건수:</span>{' '}
                {Number(pointCount).toLocaleString()}
              </div>
              <div>
                <span style={{ color: '#475569', fontWeight: 700 }}>현재 렌더링 마커:</span>{' '}
                {Number(renderedCount).toLocaleString()}
              </div>
              <div>
                <span style={{ color: '#475569', fontWeight: 700 }}>배전선로:</span>{' '}
                {showLine ? '표시' : '숨김'}
              </div>
              <div>
                <span style={{ color: '#475569', fontWeight: 700 }}>Area:</span>{' '}
                {showArea ? '표시' : '숨김'}
              </div>
            </div>
          </div>

          <div style={{ minHeight: '24px', marginTop: '12px' }}>
            {loadingMap && <div>지도 로딩 중...</div>}
            {!loadingMap && loadingPoints && <div>좌표 조회 중...</div>}
            {!loadingMap && !loadingPoints && errorMessage && (
              <div style={{ color: '#dc2626', fontWeight: 600 }}>{errorMessage}</div>
            )}
          </div>
        </div>
      </div>

      <div
        className="grid-map-frame"
        style={{
          width: '100%',
          height: '720px',
          minHeight: '520px',
          borderRadius: '18px',
          overflow: 'hidden',
          border: '1px solid #dbe2ea',
          background: '#f8fafc',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
          position: 'relative',
        }}
      >
        <div
          ref={mapElementRef}
          className="grid-map-canvas"
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </div>

      <style>{`
        .cluster-marker {
          width: 44px;
          height: 44px;
          line-height: 40px;
          border-radius: 22px;
          text-align: center;
          font-size: 13px;
          font-weight: 700;
          color: white;
          border: 2px solid rgba(255,255,255,0.95);
          box-shadow: 0 2px 8px rgba(0,0,0,0.18);
          box-sizing: border-box;
          user-select: none;
        }

        .cluster-1 { background: #60a5fa; }
        .cluster-2 { background: #3b82f6; }
        .cluster-3 { background: #2563eb; }
        .cluster-4 { background: #1d4ed8; }
        .cluster-5 { background: #1e40af; }

        @media (max-width: 900px) {
          .grid-map-top-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
